import { useDisclosureWithContext } from '@talos/kyoko';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ActionSheetProps<TContext = any> {
  trigger: (props: { open: (context?: TContext) => void; isOpen: boolean }) => ReactNode;
  content: (props: { context?: TContext | undefined; onClose: () => void }) => ReactNode;
  onClose?: () => void;
  onOpen?: () => void;
}

export function ActionSheet<TContext = any>({ trigger, content, onClose, onOpen }: ActionSheetProps<TContext>) {
  const disclosure = useDisclosureWithContext<TContext | undefined>({
    onClose,
    onOpen,
  });

  const { isOpen, open, close, context } = disclosure;

  return (
    <>
      {trigger({ open, isOpen })}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Page Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-black-100 fixed inset-0 z-40"
              onClick={close}
            />
            <ActionSheetOverlay context={context} onClose={close}>
              {renderProps => (
                <div className="border-color-border bg-color-bg-main flex flex-col rounded-t-xl border-t">
                  {content({ context: renderProps.context, onClose: renderProps.onClose })}
                </div>
              )}
            </ActionSheetOverlay>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

interface ActionSheetOverlayProps<TContext> {
  context: TContext | undefined;
  onClose: () => void;
  children: (props: { context: TContext | undefined; onClose: () => void }) => ReactNode;
}

function ActionSheetOverlay<TContext>({ context, onClose, children }: ActionSheetOverlayProps<TContext>) {
  return createPortal(
    <Fragment>
      {/* Action Sheet */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{
          duration: 0.3,
        }}
        id="action-sheet-overlay"
        data-testid="action-sheet-overlay"
        className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-hidden"
      >
        {children({ context, onClose })}
      </motion.div>
    </Fragment>,
    document.body
  );
}

// Action Sheet Item component for consistent styling
export interface ActionSheetItemProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
}
